<script setup lang="ts">
import { ref, computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API, HOST } from '@/utils/http'
import { useUserStore } from '@/stores/user'
import type { Invoice } from '@/types/bridge'

const userStore = useUserStore()
const currentEmail = computed<string>(() => userStore.member?.email?.toLowerCase() ?? '')

const emit = defineEmits<{
  created: [invoice: Invoice]
  updated: [invoice: Invoice]
}>()

const open = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
const editingId = ref<number | null>(null)

const toEmail = ref('')
const amount = ref('')
const currency = ref('USD')
const description = ref('')
const dueDate = ref('')

const isEdit = computed(() => editingId.value !== null)
const amountNum = computed(() => parseFloat(amount.value) || 0)
const isSelfInvoice = computed(() =>
  toEmail.value.trim() !== '' && toEmail.value.trim().toLowerCase() === currentEmail.value
)
const canSubmit = computed(() =>
  toEmail.value.trim() !== '' &&
  amountNum.value > 0 &&
  !isSelfInvoice.value &&
  !loading.value
)

function show(invoice?: Invoice) {
  editingId.value = invoice?.id ?? null
  toEmail.value = invoice?.to_email ?? ''
  amount.value = invoice ? parseFloat(invoice.amount).toString() : ''
  currency.value = invoice?.currency ?? 'USD'
  description.value = invoice?.description ?? ''
  dueDate.value = invoice?.due_date ?? ''
  error.value = null
  open.value = true
}

async function submit() {
  error.value = null
  loading.value = true
  try {
    const payload = {
      to_email: toEmail.value.trim(),
      amount: amountNum.value,
      currency: currency.value,
      description: description.value.trim() || null,
      due_date: dueDate.value || null,
    }
    if (isEdit.value) {
      const res = await API.patch(`${HOST}/payments/invoices/${editingId.value}`, payload)
      open.value = false
      emit('updated', res.data)
    } else {
      const res = await API.post(`${HOST}/payments/invoices`, payload)
      open.value = false
      emit('created', res.data)
    }
  } catch (err: any) {
    error.value = err.response?.data?.detail || `Failed to ${isEdit.value ? 'update' : 'create'} invoice.`
  } finally {
    loading.value = false
  }
}

defineExpose({ show })
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-md max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{{ isEdit ? 'Edit Invoice' : 'New Invoice' }}</DialogTitle>
      </DialogHeader>

      <div class="space-y-4 pt-1">
        <div class="space-y-1.5">
          <Label>Bill to <span class="text-red-500">*</span></Label>
          <Input v-model="toEmail" type="email" placeholder="recipient@example.com" />
          <p v-if="isSelfInvoice" class="text-xs text-red-600">You can't send an invoice to yourself.</p>
        </div>

        <div class="flex gap-3">
          <div class="flex-1 min-w-0 space-y-1.5">
            <Label>Amount <span class="text-red-500">*</span></Label>
            <Input v-model="amount" type="number" min="0.01" step="0.01" placeholder="0.00" />
          </div>
          <div class="w-24 shrink-0 space-y-1.5">
            <Label>Currency</Label>
            <Input v-model="currency" maxlength="10" class="uppercase" />
          </div>
        </div>

        <div class="space-y-1.5">
          <Label>Description <span class="text-muted-foreground font-normal">(optional)</span></Label>
          <Input v-model="description" placeholder="e.g. Travel reimbursement — Kigali trip June 2026" />
        </div>

        <div class="space-y-1.5">
          <Label>Due date <span class="text-muted-foreground font-normal">(optional)</span></Label>
          <Input v-model="dueDate" type="date" />
        </div>

        <p v-if="error" class="text-xs text-red-600">{{ error }}</p>
      </div>

      <DialogFooter>
        <Button variant="outline" :disabled="loading" @click="open = false">Cancel</Button>
        <Button :disabled="!canSubmit" @click="submit">
          <Loader2 v-if="loading" class="h-4 w-4 animate-spin mr-1" />
          {{ isEdit ? 'Save Changes' : 'Create Invoice' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
